import { Navigate, useNavigate } from 'react-router-dom'
import { useEffect, useReducer, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import FormError from '../components/ui/FormError'
import Button from '../components/ui/Button'
import OnboardingProgress from '../components/onboarding/OnboardingProgress'
import ProfileBasicsStep from '../components/onboarding/ProfileBasicsStep'
import ServicesStep from '../components/onboarding/ServicesStep'
import AvailabilityStep from '../components/onboarding/AvailabilityStep'
import ServiceAreasStep from '../components/onboarding/ServiceAreasStep'
import OnboardingComplete from '../components/onboarding/OnboardingComplete'
import { useLang } from '../contexts/LangContext'
import { useAuth } from '../contexts/AuthContext'
import {
  PROVIDER_ONBOARDING_QUERY,
  SET_PROVIDER_SERVICES_MUTATION,
  SET_SERVICE_AREAS_MUTATION,
  SET_WEEKLY_AVAILABILITY_MUTATION,
  UPDATE_PROFILE_MUTATION,
  UPDATE_PROVIDER_PROFILE_MUTATION,
  type ProviderOnboardingData,
  type ProviderServiceInput,
  type ServiceAreaInput,
  type SetProviderServicesData,
  type SetServiceAreasData,
  type SetWeeklyAvailabilityData,
  type UpdateProfileData,
  type UpdateProviderProfileData,
} from '../graphql/providerOnboarding'
import {
  ONBOARDING_STEP_COUNT,
  buildOnboardingState,
  createDefaultAvailability,
  isOnboardingComplete,
  type OnboardingAreaDraft,
  type OnboardingAvailabilityDay,
  type OnboardingProfileBasics,
  type OnboardingServiceDraft,
  type ProviderOnboardingState,
} from '../lib/providerOnboarding'

type OnboardingAction =
  | { type: 'hydrate'; snapshot: ProviderOnboardingData['myProviderProfile'] }
  | { type: 'set_profile_field'; field: keyof OnboardingProfileBasics; value: string }
  | { type: 'toggle_language'; code: string }
  | { type: 'toggle_service'; serviceTypeId: string }
  | {
      type: 'set_service_field'
      serviceTypeId: string
      field: keyof Pick<OnboardingServiceDraft, 'customPriceEur' | 'customDurationMinutes'>
      value: string
    }
  | { type: 'toggle_day'; dayOfWeek: number }
  | { type: 'set_day_time'; dayOfWeek: number; field: 'startTime' | 'endTime'; value: string }
  | { type: 'set_pending_postal_code'; value: string }
  | { type: 'add_area'; area: OnboardingAreaDraft }
  | { type: 'update_area'; index: number; field: keyof OnboardingAreaDraft; value: string }
  | { type: 'remove_area'; index: number }
  | { type: 'back' }
  | { type: 'advance' }
  | { type: 'complete' }

const initialState: ProviderOnboardingState = {
  hydrated: false,
  step: 0,
  profileBasics: {
    bio: '',
    yearsExperience: '',
    languages: [],
    hourlyRateEur: '',
    avatarUrl: '',
  },
  services: {},
  availability: createDefaultAvailability(),
  areas: [],
  pendingPostalCode: '',
  completedThisSession: false,
}

function reducer(state: ProviderOnboardingState, action: OnboardingAction): ProviderOnboardingState {
  switch (action.type) {
    case 'hydrate':
      return buildOnboardingState(action.snapshot)
    case 'set_profile_field':
      return {
        ...state,
        profileBasics: {
          ...state.profileBasics,
          [action.field]: action.value,
        },
      }
    case 'toggle_language': {
      const languages = state.profileBasics.languages.includes(action.code)
        ? state.profileBasics.languages.filter((language) => language !== action.code)
        : [...state.profileBasics.languages, action.code]

      return {
        ...state,
        profileBasics: {
          ...state.profileBasics,
          languages,
        },
      }
    }
    case 'toggle_service': {
      const current = state.services[action.serviceTypeId] ?? {
        selected: false,
        customPriceEur: '',
        customDurationMinutes: '',
      }

      return {
        ...state,
        services: {
          ...state.services,
          [action.serviceTypeId]: {
            ...current,
            selected: !current.selected,
          },
        },
      }
    }
    case 'set_service_field': {
      const current = state.services[action.serviceTypeId] ?? {
        selected: true,
        customPriceEur: '',
        customDurationMinutes: '',
      }

      return {
        ...state,
        services: {
          ...state.services,
          [action.serviceTypeId]: {
            ...current,
            selected: true,
            [action.field]: action.value,
          },
        },
      }
    }
    case 'toggle_day':
      return {
        ...state,
        availability: state.availability.map((day) =>
          day.dayOfWeek === action.dayOfWeek ? { ...day, active: !day.active } : day,
        ),
      }
    case 'set_day_time':
      return {
        ...state,
        availability: state.availability.map((day) =>
          day.dayOfWeek === action.dayOfWeek ? { ...day, [action.field]: action.value } : day,
        ),
      }
    case 'set_pending_postal_code':
      return {
        ...state,
        pendingPostalCode: action.value,
      }
    case 'add_area':
      return {
        ...state,
        areas: [...state.areas, action.area],
        pendingPostalCode: '',
      }
    case 'update_area':
      return {
        ...state,
        areas: state.areas.map((area, index) =>
          index === action.index ? { ...area, [action.field]: action.value } : area,
        ),
      }
    case 'remove_area':
      return {
        ...state,
        areas: state.areas.filter((_, index) => index !== action.index),
      }
    case 'back':
      return {
        ...state,
        step: Math.max(0, state.step - 1),
      }
    case 'advance':
      return {
        ...state,
        step: Math.min(ONBOARDING_STEP_COUNT - 1, state.step + 1),
      }
    case 'complete':
      return {
        ...state,
        step: ONBOARDING_STEP_COUNT,
        completedThisSession: true,
      }
    default:
      return state
  }
}

function parseInteger(value: string) {
  const trimmed = value.trim()
  if (!trimmed) return null

  const parsed = Number.parseInt(trimmed, 10)
  return Number.isNaN(parsed) ? null : parsed
}

function parseEuroToCents(value: string) {
  const trimmed = value.trim().replace(',', '.')
  if (!trimmed) return null

  const parsed = Number.parseFloat(trimmed)
  if (!Number.isFinite(parsed)) return null

  return Math.round(parsed * 100)
}

function validImageUrl(value: string) {
  if (!value.trim()) return false

  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

function compareTimes(startTime: string, endTime: string) {
  return startTime.localeCompare(endTime)
}

export default function ProviderOnboardingPage() {
  const navigate = useNavigate()
  const { t } = useLang()
  const { refetchUser } = useAuth()
  const [state, dispatch] = useReducer(reducer, initialState)
  const [stepErrors, setStepErrors] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  const {
    data,
    loading,
    error,
    refetch,
  } = useQuery<ProviderOnboardingData>(PROVIDER_ONBOARDING_QUERY)

  const [updateProfile] = useMutation<UpdateProfileData>(UPDATE_PROFILE_MUTATION)
  const [updateProviderProfile] = useMutation<UpdateProviderProfileData>(UPDATE_PROVIDER_PROFILE_MUTATION)
  const [setProviderServices] = useMutation<SetProviderServicesData>(SET_PROVIDER_SERVICES_MUTATION)
  const [setWeeklyAvailability] = useMutation<SetWeeklyAvailabilityData>(SET_WEEKLY_AVAILABILITY_MUTATION)
  const [setServiceAreas] = useMutation<SetServiceAreasData>(SET_SERVICE_AREAS_MUTATION)

  const profile = data?.myProviderProfile ?? null
  const categories = data?.serviceCategories ?? []

  useEffect(() => {
    if (!loading && !state.hydrated) {
      dispatch({ type: 'hydrate', snapshot: profile })
    }
  }, [loading, profile, state.hydrated])

  function clearStepErrors() {
    if (stepErrors.length > 0) setStepErrors([])
  }

  function handleProfileChange(field: keyof OnboardingProfileBasics, value: string) {
    clearStepErrors()
    dispatch({ type: 'set_profile_field', field, value })
  }

  function handleToggleLanguage(code: string) {
    clearStepErrors()
    dispatch({ type: 'toggle_language', code })
  }

  function handleToggleService(serviceTypeId: string) {
    clearStepErrors()
    dispatch({ type: 'toggle_service', serviceTypeId })
  }

  function handleServiceFieldChange(
    serviceTypeId: string,
    field: keyof Pick<OnboardingServiceDraft, 'customPriceEur' | 'customDurationMinutes'>,
    value: string,
  ) {
    clearStepErrors()
    dispatch({ type: 'set_service_field', serviceTypeId, field, value })
  }

  function handleToggleDay(dayOfWeek: number) {
    clearStepErrors()
    dispatch({ type: 'toggle_day', dayOfWeek })
  }

  function handleDayTimeChange(dayOfWeek: number, field: 'startTime' | 'endTime', value: string) {
    clearStepErrors()
    dispatch({ type: 'set_day_time', dayOfWeek, field, value })
  }

  function addAreaFromPostalCode(postalCode: string) {
    const normalizedPostalCode = postalCode.trim()

    if (!/^\d{5}$/.test(normalizedPostalCode)) {
      setStepErrors([t('Συμπλήρωσε έγκυρο ταχυδρομικό κώδικα 5 ψηφίων.', 'Enter a valid 5-digit postal code.')])
      return
    }

    if (state.areas.some((area) => area.postalCode === normalizedPostalCode)) {
      setStepErrors([t('Ο ταχυδρομικός κώδικας έχει ήδη προστεθεί.', 'That postal code has already been added.')])
      return
    }

    clearStepErrors()
    dispatch({
      type: 'add_area',
      area: {
        postalCode: normalizedPostalCode,
        areaName: '',
        city: t('Αθήνα', 'Athens'),
      },
    })
  }

  function handlePendingPostalCodeChange(value: string) {
    clearStepErrors()
    dispatch({ type: 'set_pending_postal_code', value })
  }

  function handleUpdateArea(index: number, field: keyof OnboardingAreaDraft, value: string) {
    clearStepErrors()
    dispatch({ type: 'update_area', index, field, value })
  }

  function handleRemoveArea(index: number) {
    clearStepErrors()
    dispatch({ type: 'remove_area', index })
  }

  function validateProfileBasics(profileBasics: OnboardingProfileBasics) {
    const yearsExperience = parseInteger(profileBasics.yearsExperience)
    const hourlyRateCents = parseEuroToCents(profileBasics.hourlyRateEur)

    const errors: string[] = []

    if (!profileBasics.bio.trim()) {
      errors.push(t('Πρόσθεσε ένα σύντομο bio.', 'Add a short bio.'))
    }
    if (yearsExperience === null || yearsExperience < 0) {
      errors.push(t('Συμπλήρωσε έγκυρα χρόνια εμπειρίας.', 'Enter valid years of experience.'))
    }
    if (profileBasics.languages.length === 0) {
      errors.push(t('Διάλεξε τουλάχιστον μία γλώσσα.', 'Pick at least one language.'))
    }
    if (hourlyRateCents === null || hourlyRateCents <= 0) {
      errors.push(t('Συμπλήρωσε έγκυρη ωριαία χρέωση.', 'Enter a valid hourly rate.'))
    }
    if (!validImageUrl(profileBasics.avatarUrl)) {
      errors.push(t('Πρόσθεσε έγκυρο URL φωτογραφίας.', 'Add a valid avatar image URL.'))
    }

    return errors
  }

  function validateServices(services: Record<string, OnboardingServiceDraft>) {
    const selectedServices = Object.entries(services).filter(([, draft]) => draft.selected)
    if (selectedServices.length === 0) {
      return [t('Διάλεξε τουλάχιστον μία υπηρεσία.', 'Choose at least one service.')]
    }

    const errors: string[] = []

    selectedServices.forEach(([, draft]) => {
      if (draft.customPriceEur.trim()) {
        const customPriceCents = parseEuroToCents(draft.customPriceEur)
        if (customPriceCents === null || customPriceCents <= 0) {
          errors.push(t('Μία ή περισσότερες προσαρμοσμένες τιμές δεν είναι έγκυρες.', 'One or more custom prices are invalid.'))
        }
      }

      if (draft.customDurationMinutes.trim()) {
        const duration = parseInteger(draft.customDurationMinutes)
        if (duration === null || duration <= 0) {
          errors.push(
            t(
              'Μία ή περισσότερες προσαρμοσμένες διάρκειες δεν είναι έγκυρες.',
              'One or more custom durations are invalid.',
            ),
          )
        }
      }
    })

    return errors
  }

  function validateAvailability(availability: OnboardingAvailabilityDay[]) {
    const activeDays = availability.filter((day) => day.active)
    if (activeDays.length === 0) {
      return [t('Ενεργοποίησε τουλάχιστον μία ημέρα.', 'Enable at least one day.')]
    }

    const errors: string[] = []

    activeDays.forEach((day) => {
      if (!day.startTime || !day.endTime || compareTimes(day.startTime, day.endTime) >= 0) {
        errors.push(t('Ένα ή περισσότερα χρονικά παράθυρα δεν είναι έγκυρα.', 'One or more time windows are invalid.'))
      }
    })

    return errors
  }

  function validateAreas(areas: OnboardingAreaDraft[]) {
    if (areas.length === 0) {
      return [t('Πρόσθεσε τουλάχιστον μία περιοχή εξυπηρέτησης.', 'Add at least one service area.')]
    }

    const errors: string[] = []
    const seenPostalCodes = new Set<string>()

    areas.forEach((area) => {
      if (!/^\d{5}$/.test(area.postalCode)) {
        errors.push(t('Όλοι οι ταχυδρομικοί κώδικες πρέπει να έχουν 5 ψηφία.', 'All postal codes must be 5 digits.'))
      }
      if (seenPostalCodes.has(area.postalCode)) {
        errors.push(t('Κάθε ταχυδρομικός κώδικας πρέπει να είναι μοναδικός.', 'Each postal code must be unique.'))
      }
      if (!area.city.trim()) {
        errors.push(t('Συμπλήρωσε πόλη για κάθε περιοχή.', 'Add a city for every area.'))
      }

      seenPostalCodes.add(area.postalCode)
    })

    return errors
  }

  async function syncOnboardingQuery() {
    try {
      await refetch()
    } catch {
      return
    }
  }

  async function handleSaveProfileBasics() {
    const errors = validateProfileBasics(state.profileBasics)
    if (errors.length > 0) {
      setStepErrors(errors)
      return
    }

    setSaving(true)
    setStepErrors([])

    try {
      const avatarResult = await updateProfile({
        variables: {
          avatarUrl: state.profileBasics.avatarUrl.trim(),
        },
      })

      const avatarErrors = avatarResult.data?.updateProfile.errors ?? [t('Δεν ήταν δυνατή η αποθήκευση της φωτογραφίας.', 'Could not save the avatar.')]
      if (!avatarResult.data?.updateProfile.user || avatarErrors.length > 0) {
        setStepErrors(avatarErrors)
        return
      }

      const yearsExperience = parseInteger(state.profileBasics.yearsExperience)
      const hourlyRateCents = parseEuroToCents(state.profileBasics.hourlyRateEur)

      const profileResult = await updateProviderProfile({
        variables: {
          bio: state.profileBasics.bio.trim(),
          yearsExperience,
          languages: state.profileBasics.languages,
          hourlyRateCents,
        },
      })

      const profileErrors = profileResult.data?.updateProviderProfile.errors ?? [t('Δεν ήταν δυνατή η αποθήκευση του προφίλ.', 'Could not save your profile.')]
      if (!profileResult.data?.updateProviderProfile.providerProfile || profileErrors.length > 0) {
        setStepErrors(profileErrors)
        return
      }

      await Promise.allSettled([syncOnboardingQuery(), refetchUser()])
      dispatch({ type: 'advance' })
    } catch {
      setStepErrors([t('Κάτι πήγε στραβά. Δοκίμασε ξανά.', 'Something went wrong. Please try again.')])
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveServices() {
    const errors = validateServices(state.services)
    if (errors.length > 0) {
      setStepErrors(errors)
      return
    }

    const services: ProviderServiceInput[] = Object.entries(state.services)
      .filter(([, draft]) => draft.selected)
      .map(([serviceTypeId, draft]) => {
        const customPriceCents = parseEuroToCents(draft.customPriceEur)
        const customDurationMinutes = parseInteger(draft.customDurationMinutes)

        return {
          serviceTypeId,
          customPriceCents: customPriceCents ?? undefined,
          customDurationMinutes: customDurationMinutes ?? undefined,
        }
      })

    setSaving(true)
    setStepErrors([])

    try {
      const result = await setProviderServices({ variables: { services } })
      const resultErrors = result.data?.setProviderServices.errors ?? [t('Δεν ήταν δυνατή η αποθήκευση των υπηρεσιών.', 'Could not save services.')]

      if (!result.data?.setProviderServices.providerProfile || resultErrors.length > 0) {
        setStepErrors(resultErrors)
        return
      }

      await syncOnboardingQuery()
      dispatch({ type: 'advance' })
    } catch {
      setStepErrors([t('Κάτι πήγε στραβά. Δοκίμασε ξανά.', 'Something went wrong. Please try again.')])
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveAvailability() {
    const errors = validateAvailability(state.availability)
    if (errors.length > 0) {
      setStepErrors(errors)
      return
    }

    const slots = state.availability
      .filter((day) => day.active)
      .map((day) => ({
        dayOfWeek: day.dayOfWeek,
        startTime: day.startTime,
        endTime: day.endTime,
      }))

    setSaving(true)
    setStepErrors([])

    try {
      const result = await setWeeklyAvailability({ variables: { slots } })
      const resultErrors = result.data?.setWeeklyAvailability.errors ?? [t('Δεν ήταν δυνατή η αποθήκευση του ωραρίου.', 'Could not save availability.')]

      if (resultErrors.length > 0) {
        setStepErrors(resultErrors)
        return
      }

      await syncOnboardingQuery()
      dispatch({ type: 'advance' })
    } catch {
      setStepErrors([t('Κάτι πήγε στραβά. Δοκίμασε ξανά.', 'Something went wrong. Please try again.')])
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveAreas() {
    const errors = validateAreas(state.areas)
    if (errors.length > 0) {
      setStepErrors(errors)
      return
    }

    const areas: ServiceAreaInput[] = state.areas.map((area) => ({
      postalCode: area.postalCode,
      areaName: area.areaName.trim() || undefined,
      city: area.city.trim(),
    }))

    setSaving(true)
    setStepErrors([])

    try {
      const result = await setServiceAreas({ variables: { areas } })
      const resultErrors = result.data?.setServiceAreas.errors ?? [t('Δεν ήταν δυνατή η αποθήκευση των περιοχών.', 'Could not save service areas.')]

      if (!result.data?.setServiceAreas.serviceAreas || resultErrors.length > 0) {
        setStepErrors(resultErrors)
        return
      }

      await syncOnboardingQuery()
      dispatch({ type: 'complete' })
    } catch {
      setStepErrors([t('Κάτι πήγε στραβά. Δοκίμασε ξανά.', 'Something went wrong. Please try again.')])
    } finally {
      setSaving(false)
    }
  }

  const stepLabels = [
    t('Βασικά στοιχεία', 'Basics'),
    t('Υπηρεσίες', 'Services'),
    t('Διαθεσιμότητα', 'Availability'),
    t('Περιοχές', 'Areas'),
  ]

  const stepDescriptions = [
    t('Δώσε στους πελάτες μια καθαρή πρώτη εικόνα.', 'Give customers a clear first impression.'),
    t('Διάλεξε ποιες υπηρεσίες αναλαμβάνεις και με ποιους όρους.', 'Choose which services you offer and on what terms.'),
    t('Όρισε το εβδομαδιαίο πρόγραμμά σου.', 'Set your weekly schedule.'),
    t('Δείξε πού μπορείς να πας για δουλειά.', 'Show where you can travel for work.'),
  ]

  if (loading || !state.hydrated) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10 md:px-6">
        <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-bold text-slate-950">
            {t('Δεν φορτώθηκε το onboarding', 'Could not load onboarding')}
          </h1>
          <p className="mt-3 text-sm leading-7 text-slate-600">
            {t(
              'Το provider onboarding δεν ήταν διαθέσιμο αυτή τη στιγμή.',
              'The provider onboarding flow was not available right now.',
            )}
          </p>
          <div className="mt-6">
            <Button type="button" onClick={() => refetch()}>
              {t('Δοκίμασε ξανά', 'Try again')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10 md:px-6">
        <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-bold text-slate-950">
            {t('Δεν βρέθηκε provider profile', 'Provider profile missing')}
          </h1>
          <p className="mt-3 text-sm leading-7 text-slate-600">
            {t(
              'Ο λογαριασμός σου δεν έχει ακόμη provider profile για να ολοκληρώσει onboarding.',
              'This account does not have a provider profile yet, so onboarding cannot continue.',
            )}
          </p>
        </div>
      </div>
    )
  }

  if (isOnboardingComplete(profile) && !state.completedThisSession) {
    return <Navigate to="/provider/dashboard" replace />
  }

  const currentStep = Math.min(state.step, ONBOARDING_STEP_COUNT - 1)

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <section className="rounded-[2rem] bg-[linear-gradient(140deg,#0f172a_0%,#0369a1_48%,#f59e0b_100%)] p-8 text-white shadow-[0_24px_60px_-36px_rgba(15,23,42,0.8)] md:p-10">
        <p className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-100">
          {t('Provider onboarding', 'Provider onboarding')}
        </p>
        <h1 className="mt-4 text-4xl font-black tracking-tight sm:text-5xl">
          {t('Στήσε το προφίλ σου και άρχισε να δέχεσαι κρατήσεις', 'Set up your profile and start receiving bookings')}
        </h1>
        <p className="mt-4 max-w-3xl text-base leading-8 text-slate-100">
          {state.step >= ONBOARDING_STEP_COUNT
            ? t(
                'Ολοκλήρωσες όλα τα απαραίτητα βήματα. Το dashboard είναι έτοιμο.',
                'You completed every required step. Your dashboard is ready.',
              )
            : stepDescriptions[currentStep]}
        </p>
      </section>

      <div className="mt-8 grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
        <div className="space-y-6">
          <OnboardingProgress currentStep={state.step} stepLabels={stepLabels} />
          <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
              {t('Τι χρειάζεται', 'What matters')}
            </p>
            <p className="mt-4 text-sm leading-7 text-slate-600">
              {t(
                'Κάθε βήμα αποθηκεύεται ξεχωριστά. Αν φύγεις στη μέση, θα επιστρέψεις ακριβώς στο πρώτο βήμα που λείπει.',
                'Each step saves separately. If you leave midway, you will return to the first missing step.',
              )}
            </p>
          </div>
        </div>

        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm md:p-8">
          {state.step < ONBOARDING_STEP_COUNT && (
            <div className="mb-8">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                {t('Τρέχον βήμα', 'Current step')}
              </p>
              <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-950">{stepLabels[currentStep]}</h2>
              <p className="mt-3 text-sm leading-7 text-slate-600">{stepDescriptions[currentStep]}</p>
            </div>
          )}

          {stepErrors.length > 0 && (
            <div className="mb-6">
              <FormError errors={stepErrors} />
            </div>
          )}

          {state.step === 0 && (
            <ProfileBasicsStep
              profileBasics={state.profileBasics}
              saving={saving}
              onChange={handleProfileChange}
              onToggleLanguage={handleToggleLanguage}
              onNext={handleSaveProfileBasics}
            />
          )}

          {state.step === 1 && (
            <ServicesStep
              categories={categories}
              services={state.services}
              saving={saving}
              onToggleService={handleToggleService}
              onChangeServiceField={handleServiceFieldChange}
              onBack={() => dispatch({ type: 'back' })}
              onNext={handleSaveServices}
            />
          )}

          {state.step === 2 && (
            <AvailabilityStep
              availability={state.availability}
              saving={saving}
              onToggleDay={handleToggleDay}
              onChangeTime={handleDayTimeChange}
              onBack={() => dispatch({ type: 'back' })}
              onNext={handleSaveAvailability}
            />
          )}

          {state.step === 3 && (
            <ServiceAreasStep
              areas={state.areas}
              pendingPostalCode={state.pendingPostalCode}
              saving={saving}
              onPendingPostalCodeChange={handlePendingPostalCodeChange}
              onAddArea={() => addAreaFromPostalCode(state.pendingPostalCode)}
              onQuickAdd={addAreaFromPostalCode}
              onUpdateArea={handleUpdateArea}
              onRemoveArea={handleRemoveArea}
              onBack={() => dispatch({ type: 'back' })}
              onNext={handleSaveAreas}
            />
          )}

          {state.step >= ONBOARDING_STEP_COUNT && (
            <OnboardingComplete onGoToDashboard={() => navigate('/provider/dashboard', { replace: true })} />
          )}
        </section>
      </div>
    </div>
  )
}
